import { useState, useMemo, useEffect, useRef } from "react";
import { FiTarget, FiCheckCircle, FiTrendingUp, FiUsers } from "react-icons/fi";
import { LuIndianRupee } from "react-icons/lu";
import { useNavigate, useLocation } from "react-router-dom";
import DynamicTable from "../Common/DynamicTable";
import { leadColumns } from "./LeadsColumn";
import api from "../../api/axios";
import LeadDrawer from "./LeadDrawer";
import Swal from "sweetalert2";
import DateFilter from "../Common/DateFilter";
import { buildDateFilterParams } from "../../utils/dateFilterUtils";
import "../../assets/css/Leads/LeadsList.css";
import "../../assets/css/Common/stats_cards.css";

const DEFAULT_CARDS = {
    total_leads: 0,
    qualified_leads: 0,
    deal_confirmation_leads: 0,
    converted_leads: 0,
    lost_leads: 0,
    expected_revenue: 0,
};

export default function LeadsList() {
    const [openCreate, setOpenCreate] = useState(false);

    const [search, setSearch] = useState("");
    const [leads, setLeads] = useState([]);
    const [cards, setCards] = useState(DEFAULT_CARDS);
    const [loading, setLoading] = useState(false);
    const [selectedIds, setSelectedIds] = useState([]);
    const [statusFilter, setStatusFilter] = useState("");
    const [typeFilter, setTypeFilter] = useState("");
    const navigate = useNavigate();
    const location = useLocation();
    const [assignedFilter, setAssignedFilter] = useState("");
    const [selectedLead, setSelectedLead] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [drawerTab, setDrawerTab] = useState("overview");
    const [loadingLeads, setLoadingLeads] = useState(false);
    const [dateFilter, setDateFilter] = useState(null);
    const requestIdRef = useRef(0);

    const stats = useMemo(() => {
        return {
            total: leads.length,
            qualified: leads.filter(
                (l) => l.status === "Qualified"
            ).length,
            pipeline: leads.filter(
                (l) =>
                    l.status !== "Lost" &&
                    l.status !== "Deal Confirmation" &&
                    l.status !== "Converted"
            ).length,
            selected: selectedIds.length,
        };
    }, [leads, selectedIds]);

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        if (location.state?.openLeadId) {
            setDrawerTab(location.state.openFollowUp ? "activity" : "overview");
            openLead(location.state.openLeadId);
        }
    }, [location.state]);

    const fetchData = async (filter = dateFilter) => {
        const currentRequestId = ++requestIdRef.current;
        setLoading(true);
        try {
            const token = localStorage.getItem("token");
            const config = {};
            if (token) config.headers = { Authorization: `Bearer ${token}` };
            const params = buildDateFilterParams(filter);
            if (Object.keys(params).length > 0) config.params = params;

            const [leadsRes, cardsRes] = await Promise.all([
                Object.keys(config).length > 0 ? api.get("/lead/list", config) : api.get("/lead/list"),
                Object.keys(config).length > 0 ? api.get("/lead/cards", config) : api.get("/lead/cards"),
            ]);

            if (currentRequestId !== requestIdRef.current) return;

            setLeads(Array.isArray(leadsRes.data) ? leadsRes.data : []);
            setCards(cardsRes.data || DEFAULT_CARDS);
        } catch (error) {
            if (currentRequestId !== requestIdRef.current) return;
            console.error("Error fetching leads:", error);
            setLeads([]);
            setCards(DEFAULT_CARDS);
        } finally {
            if (currentRequestId === requestIdRef.current) {
                setLoading(false);
            }
        }
    };

    const fetchLeads = (filter) => fetchData(filter);
    const fetchCards = (filter) => fetchData(filter);

    const handleDateFilterChange = (newFilter) => {
        setDateFilter(newFilter);
        fetchData(newFilter);
    };

    const openLead = async (id) => {
        try {
            setLoadingLeads(true);

            const response = await api.get(`/lead/get/${id}`);

            setSelectedLead(response.data);
            setDrawerOpen(true);

        } catch (error) {
            console.error("Error fetching lead:", error);

            Swal.fire({
                icon: "error",
                title: "Failed to load lead details",
            });
        } finally {
            setLoadingLeads(false);
        }
    };

    const handleSelect = (id) => {
        setSelectedIds((prev) =>
            prev.includes(id)
                ? prev.filter((x) => x !== id)
                : [...prev, id]
        );
    };

    const handleSelectAll = () => {
        if (selectedIds.length === sortedLeads.length) {
            setSelectedIds([]);
        } else {
            setSelectedIds(sortedLeads.map((lead) => lead.id));
        }
    };

    const filteredData = useMemo(() => {
        return leads.filter((lead) => {
            const safeSearch = (search || "").toLowerCase().trim();
            const matchesSearch =
                !safeSearch ||
                lead.title?.toLowerCase().includes(safeSearch) ||
                lead.lead_type?.toLowerCase().includes(safeSearch) ||
                lead.status?.toLowerCase().includes(safeSearch) ||
                lead.organization_name?.toLowerCase().includes(safeSearch) ||
                lead.contact_name?.toLowerCase().includes(safeSearch) ||
                lead.assigned_user_name?.toLowerCase().includes(safeSearch) ||
                String(lead.value || "").toLowerCase().includes(safeSearch) ||
                String(lead.id || "").includes(safeSearch);

            const matchesStatus = statusFilter
                ? lead.status === statusFilter
                : true;

            const matchesType = typeFilter
                ? lead.lead_type === typeFilter
                : true;

            const matchesAssigned = assignedFilter
                ? String(lead.assigned_to) === String(assignedFilter)
                : true;

            return (
                matchesSearch &&
                matchesStatus &&
                matchesType &&
                matchesAssigned
            );
        });
    }, [leads, search, statusFilter, typeFilter, assignedFilter]);

const user = JSON.parse(sessionStorage.getItem("user"));
const canDelete =
  user?.role !== "Sales Executive" &&
  user?.role !== "Sales Manager";


    //bulk delete

    const handleBulkDelete = async () => {
        if (!selectedIds.length) return;

        const result = await Swal.fire({
            title: "Delete selected leads?",
            text: `You are about to delete ${selectedIds.length} leads.`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, delete",
        });

        if (!result.isConfirmed) return;

        try {
            setLoading(true);

            await api.post(
                "/lead/bulk-delete",
                { lead_ids: selectedIds },
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("token")}`,
                    },
                }
            );

            // remove deleted leads from UI
            setLeads((prev) =>
                prev.filter((lead) => !selectedIds.includes(lead.id))
            );

            setSelectedIds([]);

            Swal.fire({
                icon: "success",
                title: "Leads deleted successfully",
            });
        } catch (error) {
            console.error("Bulk delete failed:", error);

            Swal.fire({
                icon: "error",
                title: "Failed to delete leads",
            });
        } finally {
            setLoading(false);
        }
    };



    const statuses = useMemo(() => {
        return [
            ...new Set(
                leads
                    .map((lead) => lead.status)
                    .filter(Boolean)
            ),
        ];
    }, [leads]);

    const types = useMemo(() => {
        return [...new Set(leads.map((l) => l.lead_type).filter(Boolean))];
    }, [leads]);

    const [sortConfig, setSortConfig] = useState({
        key: null,
        direction: "asc",
    });


    const getSortValue = (lead, key) => {
        switch (key) {
            case "title":
                return `${lead.title ?? ""} ${lead.organization?.name ?? ""}`.trim();

            case "status":
                return lead.status;

            case "source":
                return lead.source;

            case "lead_type":
                return lead.lead_type;

            case "value":
                return Number(lead.value || 0);

            case "assigned":
                return lead.assigned_user?.name || lead.assigned_user?.email || "";

            case "next_followup_date": {
                const followUp = lead.next_followup_date || lead.next_follow_up;
                return followUp ? new Date(followUp).getTime() : 0;
            }

            case "created_at":
                return lead.created_at ? new Date(lead.created_at).getTime() : 0;

            default:
                return lead[key];
        }
    };

    const sortedLeads = useMemo(() => {
        if (!sortConfig.key) return filteredData;

        return [...filteredData].sort((a, b) => {
            const aVal = getSortValue(a, sortConfig.key);
            const bVal = getSortValue(b, sortConfig.key);

            if (aVal == null) return 1;
            if (bVal == null) return -1;

            if (typeof aVal === "number") {
                return sortConfig.direction === "asc"
                    ? aVal - bVal
                    : bVal - aVal;
            }

            return sortConfig.direction === "asc"
                ? String(aVal).localeCompare(String(bVal))
                : String(bVal).localeCompare(String(aVal));
        });
    }, [filteredData, sortConfig]);


    const handleSortChange = (key) => {
        setSortConfig((prev) => ({
            key,
            direction:
                prev.key === key && prev.direction === "asc"
                    ? "desc"
                    : "asc",
        }));
    };

    return (
        <div className="main-wrapper">

            {/* HEADER */}
            <div className="page-header">
                <h2>Leads</h2>
                <DateFilter value={dateFilter} onChange={handleDateFilterChange} />
            </div>

            {/* STATS */}
            <div className={`stats-grid stats-grid-5${loading ? " refreshing" : ""}`}>
                <div className="stat-card">
                    <div className="stat-icon blue">
                        <FiTarget />
                    </div>

                    <div>

                        <p>Total Leads</p>
                        <h2>{cards?.total_leads ?? 0}</h2>
                    </div>
                </div>

                <div className="stat-card">
                    <div className="stat-icon purple">
                        <FiCheckCircle />
                    </div>

                    <div>

                        <p>Qualified Leads</p>
                        <h2>{cards?.qualified_leads ?? 0}</h2>
                    </div>
                </div>

                <div className="stat-card">
                    <div className="stat-icon green">
                        <FiTrendingUp />
                    </div>

                    <div>

                        <p>Deal Confirmation</p>
                        <h2>{cards?.deal_confirmation_leads ?? cards?.converted_leads ?? 0}</h2>
                    </div>
                </div>

                <div className="stat-card">
                    <div className="stat-icon gold">
                        <FiUsers />
                    </div>

                    <div>

                        <p>Lost Leads</p>
                        <h2>{cards?.lost_leads ?? 0}</h2>
                    </div>
                </div>

                <div className="stat-card">
                    <div className="stat-icon gold">
                        <LuIndianRupee />
                    </div>

                    <div>

                        <p>Expected Revenue</p>
                        <h2>{Number(cards?.expected_revenue || 0).toLocaleString("en-IN")}</h2>
                    </div>
                </div>
            </div>



            {/* TABLE */}
            <div className="table-card">
                <DynamicTable
                    data={sortedLeads}
                    columns={leadColumns({
                        selectedIds,
                        handleSelect,
                        handleSelectAll,
                        totalRows: sortedLeads.length,
                    })}
                    search={search}
                    onSearchChange={setSearch}
                    searchPlaceholder="Search leads..."
                    onAdd={() => navigate("/leads/create")}
                    addButtonText="Add Lead"
                    onRowClick={(id) => {
                        setDrawerTab("overview");
                        openLead(id);
                    }}
                    onBulkDelete={handleBulkDelete}
                    selectedIds={selectedIds}
                    selectedCount={selectedIds.length}
                    sortConfig={sortConfig}
                    onSortChange={handleSortChange}
                    storageKey="leals_columns"
                    canDelete={canDelete}
                    filters={[
                        {
                            name: "status",
                            value: statusFilter,
                            onChange: setStatusFilter,
                            options: statuses,
                            placeholder: "All Status",
                        },
                        {
                            name: "type",
                            value: typeFilter,
                            onChange: setTypeFilter,
                            options: types,
                            placeholder: "All Types",
                        },

                    ]}
                />
            </div>
            <LeadDrawer
                lead={selectedLead}
                isOpen={drawerOpen}
                onClose={() => {
                    setDrawerOpen(false);
                    setDrawerTab("overview");
                }}
                refreshLeads={fetchLeads}
                initialTab={drawerTab}
            />
        </div>
    );
}